import React, { useState, useEffect } from "react";
import { Menu, X, ShoppingBag } from "lucide-react";

const navLinks = [
  { label: "Inicio", href: "#home" },
  { label: "Catálogo", href: "#catalogo" },
  { label: "Personalizar", href: "#personalizar" },
  { label: "Workshop", href: "#workshop", badge: "Nuevo" },
  { label: "Nosotros", href: "#nosotros" },
  { label: "Contacto", href: "#contacto" },
];

interface NavbarProps {
  isWorkshopVisible: boolean;
}

const Navbar: React.FC<NavbarProps> = ({ isWorkshopVisible }) => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeHash, setActiveHash] = useState(() => window.location.hash || "#home");
  const visibleNavLinks = navLinks.filter(
    (link) => link.href !== "#workshop" || isWorkshopVisible,
  );

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 50);
      if (window.scrollY < 120 && !window.location.hash) {
        setActiveHash("#home");
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onHash = () => {
      const h = window.location.hash || "#home";
      setActiveHash(h);
    };
    window.addEventListener("hashchange", onHash);
    window.addEventListener("popstate", onHash);
    return () => {
      window.removeEventListener("hashchange", onHash);
      window.removeEventListener("popstate", onHash);
    };
  }, []);

  const handleNavClick = (href: string) => {
    setMenuOpen(false);
    setActiveHash(href);
    if (href === "#home") {
      window.history.replaceState(null, "", " ");
      window.scrollTo({ top: 0, behavior: "smooth" });
      // Sync ContentHub back to the catalog (home content) when leaving other tabs
      window.dispatchEvent(new HashChangeEvent("hashchange"));
      return;
    }
    window.history.replaceState(null, "", href);
    if (href === "#personalizar" || href === "#custom-cakes") {
      const customCakesEl = document.getElementById("custom-cakes");
      if (customCakesEl) {
        const yOffset = -70;
        const y = customCakesEl.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: "smooth" });
      }
      window.dispatchEvent(new HashChangeEvent("hashchange"));
      return;
    }
    const hub = document.getElementById("hub");
    if (hub) {
      const yOffset = -70;
      const y = hub.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
    window.dispatchEvent(new HashChangeEvent("hashchange"));
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || menuOpen
          ? "bg-white shadow-lg shadow-rose-950/5 border-b border-rose-100"
          : "bg-transparent"
      }`}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a
            href="#home"
            onClick={(e) => {
              e.preventDefault();
              handleNavClick("#home");
            }}
            className="flex items-center group"
          >
            <img
              src="/Estef Cakesdsds.png"
              alt="Estef's Cakes"
              className="h-14 md:h-16 w-auto group-hover:scale-105 transition-transform duration-300"
            />
          </a>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-7 lg:gap-8">
            {visibleNavLinks.map((link) => {
              const isActive = activeHash === link.href;
              return (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => {
                    e.preventDefault();
                    handleNavClick(link.href);
                  }}
                  className={`font-body text-sm relative py-1 transition-all duration-200 flex items-center gap-1.5 group ${
                    isActive
                      ? "text-rose-700 font-bold"
                      : "text-rose-900/70 hover:text-rose-700 font-medium"
                  }`}
                >
                  <span>{link.label}</span>
                  {link.badge && (
                    <span className="text-[9px] bg-rose-600 text-white font-bold px-1.5 py-0.5 rounded-full uppercase tracking-wider leading-none">
                      {link.badge}
                    </span>
                  )}
                  <span
                    className={`absolute -bottom-1 left-0 h-0.5 bg-rose-500 rounded-full transition-all duration-300 ${
                      isActive ? "w-full" : "w-0 group-hover:w-full"
                    }`}
                  />
                </a>
              );
            })}
            <a
              href="#contacto"
              onClick={(e) => {
                e.preventDefault();
                handleNavClick("#contacto");
              }}
              className="bg-rose-700 hover:bg-rose-800 text-white font-body text-sm px-5 py-2.5 rounded-full transition-all duration-300 hover:shadow-lg hover:shadow-rose-200 flex items-center gap-2 active:scale-95">
              <ShoppingBag className="w-4 h-4" />
              Pedir ahora
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-rose-800 hover:bg-rose-50 rounded-xl transition-colors"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label={menuOpen ? "Cerrar menú" : "Abrir menú"}
            aria-expanded={menuOpen}>
            {menuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu Dropdown — 100% Opaco, sin transparencias que dejen ver el texto del Hero */}
      {menuOpen && (
        <div className="md:hidden bg-white border-t border-rose-100 px-5 pb-7 pt-2 shadow-2xl animate-fadeIn">
          {visibleNavLinks.map((link) => {
            const isActive = activeHash === link.href;
            return (
              <a
                key={link.href}
                href={link.href}
                className={`flex items-center justify-between py-3.5 font-body text-base border-b border-rose-100/60 last:border-0 transition-colors ${
                  isActive
                    ? "text-rose-700 font-bold"
                    : "text-rose-950 font-semibold hover:text-rose-700"
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick(link.href);
                }}>
                <span>{link.label}</span>
                {link.badge && (
                  <span className="text-[10px] bg-rose-600 text-white font-bold px-2 py-0.5 rounded-full uppercase">
                    {link.badge}
                  </span>
                )}
              </a>
            );
          })}
          <a
            href="#contacto"
            className="mt-5 w-full bg-rose-700 hover:bg-rose-800 active:scale-[0.98] text-white font-body font-bold text-sm px-5 py-3.5 rounded-2xl flex items-center justify-center gap-2 shadow-md shadow-rose-900/10 transition-all"
            onClick={(e) => {
              e.preventDefault();
              handleNavClick("#contacto");
            }}>
            <ShoppingBag className="w-4 h-4" />
            Pedir ahora
          </a>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
